import Modal from 'react-bootstrap/Modal';
import { useEffect, useState } from 'react';
import { getMenuImageUrl } from '../../utils/menuImageUrl';

type Props = {
  show: boolean;
  hide: () => void;
  details: any;
};

function ViewMenuItemModal({ show, hide, details }: Props) {
  const [selectedMenuItem, setSelectedMenuItem] = useState<any>({});

  useEffect(() => {
    if (show && details) {
      setSelectedMenuItem(details);
    }
  }, [details, show]);

  const remainingStock = selectedMenuItem ?
    (selectedMenuItem.stockQuantity || 0) - (selectedMenuItem.soldQuantity || 0) - (selectedMenuItem.givenQuantity || 0)
    : 0;

  return (
    <Modal show={show} onHide={hide} size="xl" centered>
      <Modal.Body className="p-0">
        <div className="bg-slate-900 px-6 py-5 rounded-t-xl flex justify-between items-center">
          <h3 className="text-xl font-bold text-white tracking-wide">
            {selectedMenuItem?.deviceName || selectedMenuItem?.name}
          </h3>
          {remainingStock > 0 && selectedMenuItem?.isAvailable ? (
            <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full text-xs font-bold uppercase tracking-wider">
              Available
            </span>
          ) : (
            <span className="px-3 py-1 bg-rose-500/20 text-rose-300 border border-rose-500/30 rounded-full text-xs font-bold uppercase tracking-wider">
              Not Available
            </span>
          )}
        </div>

        <div className="p-6 md:p-8 space-y-8 bg-white">
          {/* Main Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Sub Category</div>
              <div className="font-semibold text-slate-800">{selectedMenuItem?.type || selectedMenuItem?.category || '—'}</div>
            </div>
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Main Category</div>
              <div>
                {(() => {
                  const ctype = selectedMenuItem?.categoryId?.type || 'Regular';
                  return (
                    <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${
                      ctype === 'Snacks' ? 'bg-orange-100 text-orange-800' 
                      : ctype === 'Drinks' ? 'bg-blue-100 text-blue-800' 
                      : 'bg-slate-100 text-slate-800'
                    }`}>
                      {ctype}
                    </span>
                  );
                })()}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Price</div>
              <div className="font-bold text-slate-800">Rwf {selectedMenuItem?.price?.toLocaleString() || 0}</div>
            </div>
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Given Output</div>
              <div className="font-semibold text-emerald-600">{selectedMenuItem?.givenQuantity || 0} items</div>
            </div>
          </div>

          {/* New Inventory Overview Section */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h4 className="text-sm font-bold text-slate-800 uppercase tracking-widest border-b border-slate-200 pb-3 mb-5">
              Inventory Overview
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-center">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Total Lifetime Stock</div>
                <div className="text-3xl font-black text-slate-800">
                  {selectedMenuItem?.stockQuantity || 0} <span className="text-sm font-semibold text-slate-400">units</span>
                </div>
              </div>
              
              <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-blue-500 rounded-full blur-3xl opacity-10"></div>
                <div className="text-[11px] font-bold text-blue-500 uppercase tracking-wider mb-1">Lifetime Sold</div>
                <div className="text-3xl font-black text-blue-600">
                  {selectedMenuItem?.soldQuantity || 0} <span className="text-sm font-semibold text-blue-400">units</span>
                </div>
              </div>

              <div className={`p-5 rounded-xl border shadow-sm flex flex-col justify-center relative overflow-hidden ${
                remainingStock > 0 ? 'bg-white border-emerald-100' : 'bg-rose-50 border-rose-100'
              }`}>
                {remainingStock > 0 && <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500 rounded-full blur-3xl opacity-10"></div>}
                <div className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${remainingStock > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  Current Available
                </div>
                <div className={`text-3xl font-black flex items-baseline gap-2 ${remainingStock > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {remainingStock}
                  <span className={`text-sm font-bold ${remainingStock > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    / {selectedMenuItem?.stockQuantity || 0}
                  </span>
                </div>
                {remainingStock === 0 && (
                  <div className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mt-2">Sold Out</div>
                )}
              </div>
            </div>

            {/* Restock History Timeline */}
            <div className="mt-8">
              <h5 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Stock Batch History</h5>
              
              {selectedMenuItem?.stockHistory && selectedMenuItem.stockHistory.length > 0 ? (
                <div className="relative pl-6 border-l-[3px] border-slate-200 space-y-6 ml-3">
                  {[...selectedMenuItem.stockHistory].reverse().map((history: any, index: number) => {
                    const isNewest = index === 0;
                    return (
                      <div key={index} className="relative transition-all hover:-translate-y-1">
                        <div className={`absolute -left-[30.5px] mt-1.5 w-4 h-4 rounded-full border-2 border-white ${
                          isNewest ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.2)]' : 'bg-slate-300'
                        }`}></div>
                        <div className={`bg-white rounded-xl p-4 shadow-sm border ${isNewest ? 'border-emerald-100' : 'border-slate-100'}`}>
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-3">
                              <span className={`text-sm font-bold ${isNewest ? 'text-slate-800' : 'text-slate-600'}`}>
                                {history.note || 'Restock'}
                              </span>
                              {isNewest && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-black uppercase rounded text-center tracking-widest">
                                  Latest Batch
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-slate-400 font-semibold bg-slate-50 px-2 py-1 rounded">
                              {new Date(history.date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-6 bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <div className="flex flex-col">
                              <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Added</span>
                              <span className="font-black text-emerald-600 text-base">+{history.addedQuantity}</span>
                            </div>
                            <div className="w-px h-8 bg-slate-200"></div>
                            <div className="flex flex-col">
                              <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Previous Total</span>
                              <span className="font-bold text-slate-600 text-base">{history.previousTotalStock}</span>
                            </div>
                            <div className="w-px h-8 bg-slate-200"></div>
                            <div className="flex flex-col">
                              <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">New Total Stock</span>
                              <span className="font-black text-slate-800 text-base">{history.newTotalStock}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-slate-400 font-medium italic bg-white p-5 rounded-xl flex items-center justify-center border border-slate-200 border-dashed">
                  No detailed stock history found. Updates after today will be logged here.
                </div>
              )}
            </div>
          </div>

          {selectedMenuItem?.images && selectedMenuItem.images.length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">Product Images</h4>
              <div className="flex gap-4 flex-wrap">
                {selectedMenuItem.images.map((el: any, index: number) => (
                  <div key={`image-${el.id || el._id || index}`} className="relative group">
                    <img
                      src={getMenuImageUrl(el.url)}
                      className="w-32 h-32 object-contain bg-slate-50 rounded-xl border border-slate-200 shadow-sm group-hover:shadow-md transition-shadow"
                      alt={`${selectedMenuItem.deviceName || selectedMenuItem.name}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ViewMenuItemModal;
